import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Checkbox } from '../ui/checkbox';
import { ScrollArea } from '../ui/scroll-area';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { RadioGroup, RadioGroupItem } from '../ui/radio-group';
import { toast } from 'sonner';
import * as XLSX from 'xlsx';
import {
  FileText,
  FileSpreadsheet,
  CheckCircle2,
  AlertTriangle,
  XCircle,
  X,
  ChevronDown,
  ChevronRight,
  Loader2,
  ArrowLeft,
  ArrowRight,
  Download,
  Printer,
  Upload,
} from 'lucide-react';
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from '../ui/dropdown-menu';
import { newLandscapeDoc, addBrandHeader, addFooters, loadLogoPng, exportTableHtml, SLATE_900, SLATE_600, SLATE_500, BLUE, BORDER } from '../../utils/tripExport';
/* ---------------------------------------------------------- helpers */

const ROLE_MAP = {
  'super admin': 'super_admin', 'super_admin': 'super_admin',
  'administrator': 'admin', 'admin': 'admin',
  'manager': 'manager',
  'end user': 'end_user', 'end_user': 'end_user',
  'sub account': 'sub_account', 'sub_account': 'sub_account',
};

const ROLE_LABELS = {
  super_admin: 'Super Admin',
  admin: 'Administrator',
  manager: 'Manager',
  end_user: 'End User',
  sub_account: 'Sub Account',
};

// Fields we know how to import. Everything else in the CSV is shown as "ignored".
const IMPORT_FIELD_KEYS = new Set([
  'account_reference', 'username', 'full_name', 'email', 'role',
  'is_active', 'expiry_enabled', 'expiry_date',
  'company', 'phone', 'mobile',
  'address_street', 'address_city', 'address_postcode', 'address_state', 'address_country',
  'notes', 'password', 'assigned_device_names', 'assigned_beacon_names',
  'assigned_device_count', 'assigned_beacon_count',
  'history_retention_days',
  'max_devices', 'max_beacons', 'max_drivers', 'max_staff', 'max_passengers', 'max_end_users', 'max_sub_accounts',
  'enabled_features',
]);

// Privilege / resource-limit columns (round-trip with the Users export;
// values accept a number, 'Unlimited' (=-1) or 'Not Set' (=0)).
const LIMIT_FIELD_KEYS = [
  'history_retention_days',
  'max_devices', 'max_beacons', 'max_drivers', 'max_staff',
  'max_passengers', 'max_end_users', 'max_sub_accounts',
];

const LIMIT_LABELS = {
  history_retention_days: 'History Retention (days)',
  max_devices: 'Max Devices',
  max_beacons: 'Max Beacons',
  max_drivers: 'Max Drivers',
  max_staff: 'Max Staff',
  max_passengers: 'Max Passengers',
  max_end_users: 'Max End Users',
  max_sub_accounts: 'Max Sub Accounts',
};

const parseLimitValue = (raw) => {
  const s = String(raw ?? '').trim();
  if (!s) return { value: null };
  const low = s.toLowerCase();
  if (low === 'unlimited') return { value: -1 };
  if (low === 'not set') return { value: 0 };
  const n = parseInt(s, 10);
  if (Number.isNaN(n)) return { value: null, invalid: true };
  return { value: n };
};

// Friendly header (any casing, may include the "(Read Only)" export marker) → canonical field.
const HEADER_ALIASES = {
  'account_reference': 'account_reference',
  'username': 'username',
  'full_name': 'full_name',
  'email': 'email',
  'role': 'role',
  'status': 'is_active',
  'is_active': 'is_active',
  'expiry_enabled': 'expiry_enabled',
  'expiry_date': 'expiry_date',
  'company': 'company',
  'phone': 'phone',
  'mobile': 'mobile',
  'street_address': 'address_street',
  'address_street': 'address_street',
  'city': 'address_city',
  'address_city': 'address_city',
  'postcode': 'address_postcode',
  'address_postcode': 'address_postcode',
  'state': 'address_state',
  'address_state': 'address_state',
  'country': 'address_country',
  'address_country': 'address_country',
  'notes': 'notes',
  'password': 'password',
  'assigned_device_names': 'assigned_device_names',
  'assigned_devices': 'assigned_device_names',
  'assigned_beacon_names': 'assigned_beacon_names',
  'assigned_beacons': 'assigned_beacon_names',
  'assigned_device_count': 'assigned_device_count',
  'assigned_devices_count': 'assigned_device_count',
  'assigned_beacon_count': 'assigned_beacon_count',
  'assigned_beacons_count': 'assigned_beacon_count',
  'history_retention_days': 'history_retention_days',
  'max_devices': 'max_devices',
  'max_beacons': 'max_beacons',
  'max_drivers': 'max_drivers',
  'max_staff': 'max_staff',
  'max_passengers': 'max_passengers',
  'max_end_users': 'max_end_users',
  'max_sub_accounts': 'max_sub_accounts',
  'enabled_features': 'enabled_features',
};

const normalizeHeader = (h) =>
  (h || '')
    .toLowerCase()
    .replace(/\s*\(read only\)\s*/gi, '')
    .trim()
    .replace(/[\s-]+/g, '_');

const parseCsvLine = (line) => {
  const values = [];
  let current = '';
  let inQuotes = false;
  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (ch === '"') {
      if (inQuotes && line[i + 1] === '"') { current += '"'; i++; }
      else { inQuotes = !inQuotes; }
    } else if (ch === ',' && !inQuotes) {
      values.push(current);
      current = '';
    } else {
      current += ch;
    }
  }
  values.push(current);
  return values.map(v => v.trim().replace(/^=/, '').replace(/^"|"$/g, ''));
};

const isEmailValid = (s) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(s || '').trim());

/* ---------------------------------------------------------- templates */

export const USER_TEMPLATE_HEADERS = [
  'account_reference', 'username', 'full_name', 'email', 'role',
  'is_active', 'expiry_enabled', 'expiry_date',
  'company', 'phone', 'mobile',
  'address_street', 'address_city', 'address_postcode', 'address_state', 'address_country',
  'notes', 'password',
  'history_retention_days',
  'max_devices', 'max_beacons', 'max_drivers', 'max_staff', 'max_passengers', 'max_end_users', 'max_sub_accounts',
  'enabled_features',
  'assigned_device_count', 'assigned_device_names',
  'assigned_beacon_count', 'assigned_beacon_names',
];

const USER_EXAMPLE_ROW = {
  account_reference: 'ACC-1001',
  username: 'jsmith',
  full_name: 'John Smith',
  email: 'john.smith@example.com',
  role: 'End User',
  is_active: 'Active',
  expiry_enabled: 'No',
  expiry_date: '',
  company: 'Acme Transport',
  phone: '0298765432',
  mobile: '0412345678',
  address_street: '1 Example St',
  address_city: 'Sydney',
  address_postcode: '2000',
  address_state: 'NSW',
  address_country: 'Australia',
  notes: 'Imported example row',
  password: '',
  history_retention_days: '90',
  max_devices: '5',
  max_beacons: 'Unlimited',
  max_drivers: 'Not Set',
  max_staff: 'Not Set',
  max_passengers: 'Not Set',
  max_end_users: 'Not Set',
  max_sub_accounts: '2',
  enabled_features: 'live_tracking; history',
  assigned_device_count: '2',
  assigned_device_names: 'Truck 22; Van 3',
  assigned_beacon_count: '1',
  assigned_beacon_names: 'Pallet Beacon 1',
};

// Columns that MUST stay text even after an Excel round-trip (leading zeros, long digits)
const USER_XLSX_TEXT_FIELDS = new Set(['phone', 'mobile', 'address_postcode', 'account_reference', 'password']);

const userCsvCell = (v, isText) => {
  const s = String(v ?? '');
  if (isText && s) return `="${s.replace(/"/g, '""')}"`;
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

export const downloadUserImportTemplate = (withExample = false) => {
  const lines = [USER_TEMPLATE_HEADERS.join(',')];
  if (withExample) {
    lines.push(USER_TEMPLATE_HEADERS.map(h => userCsvCell(USER_EXAMPLE_ROW[h] ?? '', USER_XLSX_TEXT_FIELDS.has(h))).join(','));
  }
  const blob = new Blob(['\uFEFF' + lines.join('\n') + '\n'], { type: 'text/csv;charset=utf-8;' });
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = withExample ? 'users_import_example.csv' : 'users_import_template.csv';
  a.click();
  window.URL.revokeObjectURL(url);
};

export const downloadUserImportTemplateXlsx = (withExample = false) => {
  const dataRow = USER_TEMPLATE_HEADERS.map(h => (withExample ? String(USER_EXAMPLE_ROW[h] ?? '') : ''));
  const rows = [USER_TEMPLATE_HEADERS, dataRow];
  for (let i = 0; i < 200; i++) rows.push(USER_TEMPLATE_HEADERS.map(() => ''));
  const ws = XLSX.utils.aoa_to_sheet(rows);
  ws['!cols'] = USER_TEMPLATE_HEADERS.map((h) => ({ wch: (h.length < 14 ? 14 : Math.min(h.length + 4, 28)) }));
  const range = XLSX.utils.decode_range(ws['!ref']);
  USER_TEMPLATE_HEADERS.forEach((h, colIdx) => {
    if (!USER_XLSX_TEXT_FIELDS.has(h)) return;
    for (let r = range.s.r; r <= range.e.r; r++) {
      const addr = XLSX.utils.encode_cell({ c: colIdx, r });
      if (!ws[addr]) ws[addr] = { t: 's', v: '', z: '@' };
      else { ws[addr].t = 's'; ws[addr].z = '@'; ws[addr].v = String(ws[addr].v ?? ''); }
    }
  });
  ws['!freeze'] = { xSplit: 0, ySplit: 1, topLeftCell: 'A2', activePane: 'bottomLeft', state: 'frozen' };
  const wb = XLSX.utils.book_new();
  wb.Props = { Title: 'EnsueLIVETRAK User Import Template', Subject: 'User import (Excel-safe)', Author: 'EnsueLIVETRAK', CreatedDate: new Date() };
  XLSX.utils.book_append_sheet(wb, ws, 'Import');
  XLSX.writeFile(wb, withExample ? 'users_import_example.xlsx' : 'users_import_template.xlsx');
};

/* ---------------------------------------------------------- shared file reader (wizard + Quick Import) */

export const readUserImportFile = (f) => new Promise((resolve, reject) => {
  const isXlsx = /\.xlsx$/i.test(f.name) || f.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
  const finish = (aoa) => {
    if (!aoa || aoa.length < 2) { reject(new Error(`${isXlsx ? 'XLSX' : 'CSV'} file has no data rows`)); return; }
    const headerCells = aoa[0].map(v => String(v ?? ''));
    const canonical = headerCells.map(h => HEADER_ALIASES[normalizeHeader(h)] || null);
    const detected = [];
    const ignored = [];
    headerCells.forEach((orig, idx) => {
      const key = canonical[idx];
      if (key && IMPORT_FIELD_KEYS.has(key)) detected.push({ original: orig, key });
      else ignored.push(orig);
    });
    const rows = [];
    for (let i = 1; i < aoa.length; i++) {
      const values = aoa[i] || [];
      if (values.every(v => String(v ?? '').trim() === '')) continue;
      const data = {};
      canonical.forEach((key, idx) => {
        if (!key) return;
        data[key] = String(values[idx] ?? '').trim().replace(/^=/, '').replace(/^"|"$/g, '');
      });
      rows.push({ rowIndex: i, data });
    }
    resolve({ rows, detected, ignored, isXlsx });
  };
  const reader = new FileReader();
  if (isXlsx) {
    reader.onload = (ev) => {
      try {
        const wb = XLSX.read(ev.target.result, { type: 'array' });
        const ws = wb.Sheets[wb.SheetNames[0]];
        finish(XLSX.utils.sheet_to_json(ws, { header: 1, raw: false, defval: '' }));
      } catch (e) { reject(new Error('Failed to parse XLSX file')); }
    };
    reader.readAsArrayBuffer(f);
  } else {
    reader.onload = (ev) => {
      try {
        const text = String(ev.target?.result || '').replace(/^\uFEFF/, '');
        const lines = text.split(/\r?\n/).filter((l, i) => i === 0 || l.trim().length > 0);
        finish(lines.map(parseCsvLine));
      } catch (e) { reject(new Error('Failed to parse CSV file')); }
    };
    reader.readAsText(f);
  }
});

/* ---------------------------------------------------------- component */

export default function UserImportWizard({
  open,
  onOpenChange,
  users,
  allDevices,
  allBeacons,
  featureCatalog = [],
  api,
  getDateFormat,
  onCompleted,
}) {
  const [step, setStep] = useState(1);
  const [file, setFile] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [parsedHeaders, setParsedHeaders] = useState([]);
  const [ignoredHeaders, setIgnoredHeaders] = useState([]);
  const [rows, setRows] = useState([]); // [{ id, rowIndex, data, existingUserId, resolvedDeviceIds, missingDeviceNames, errors, warnings, selected, expanded }]
  const [duplicateMode, setDuplicateMode] = useState('skip'); // 'skip' | 'update'
  const [fieldMode, setFieldMode] = useState('merge'); // 'merge' | 'replace' — how updates treat existing values
  const [dryRun, setDryRun] = useState(false); // simulate the import without saving anything
  const [progress, setProgress] = useState({ current: 0, total: 0 });
  const [importing, setImporting] = useState(false);
  const [results, setResults] = useState(null); // { successCreate, successUpdate, failed, errors: [{email, message}] }
  const fileInputRef = useRef(null);

  /* Reset everything when opening/closing */
  useEffect(() => {
    if (!open) return;
    setStep(1);
    setFile(null);
    setParsedHeaders([]);
    setIgnoredHeaders([]);
    setRows([]);
    setDuplicateMode('skip');
    setFieldMode('merge');
    setDryRun(false);
    setProgress({ current: 0, total: 0 });
    setImporting(false);
    setResults(null);
  }, [open]);

  /* Lookup indexes for O(1) validation */
  // The `users` prop only holds the current PAGE — fetch the full list on open
  // so duplicate detection sees every existing user, not just the visible page.
  const [fullUsers, setFullUsers] = useState([]);
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    api.get('/users?limit=100000&skip=0')
      .then(res => { if (!cancelled && Array.isArray(res.data)) setFullUsers(res.data); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [open, api]);

  const emailToUser = useMemo(() => {
    const m = new Map();
    const source = fullUsers.length > 0 ? fullUsers : (users || []);
    source.forEach(u => { if (u.email) m.set(String(u.email).toLowerCase(), u); });
    return m;
  }, [users, fullUsers]);

  const deviceByName = useMemo(() => {
    const m = new Map();
    (allDevices || []).forEach(d => {
      if (d && d.name) m.set(String(d.name).toLowerCase().trim(), d);
    });
    return m;
  }, [allDevices]);

  // Re-validate parsed rows once the full user list arrives — closes the race
  // where a file uploaded before the fetch resolved would miss duplicates.
  useEffect(() => {
    if (fullUsers.length === 0) return;
    setRows(prev => {
      if (prev.length === 0) return prev;
      return prev.map(r => {
        const v = validateRow(r.data);
        const selected = v.existingUserId && !r.existingUserId ? false : r.selected;
        return { ...r, ...v, selected };
      });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fullUsers]);

  const beaconByName = useMemo(() => {
    const m = new Map();
    (allBeacons || []).forEach(d => {
      if (d && d.name) m.set(String(d.name).toLowerCase().trim(), d);
    });
    return m;
  }, [allBeacons]);

  /* -------- date parsing (matches existing importer) */
  const parseImportedDate = useCallback((raw) => {
    if (!raw) return null;
    const s = String(raw).trim();
    if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s;
    const m = s.match(/^(\d{1,2})[/.-](\d{1,2})[/.-](\d{4})/);
    if (!m) return null;
    const fmt = (typeof getDateFormat === 'function' ? getDateFormat() : 'DD/MM/YYYY') || 'DD/MM/YYYY';
    const mdFirst = String(fmt).toUpperCase().startsWith('MM');
    const day = mdFirst ? m[2] : m[1];
    const month = mdFirst ? m[1] : m[2];
    return `${m[3]}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}T00:00:00`;
  }, [getDateFormat]);

  /* -------- validate a single row */
  const validateRow = useCallback((data) => {
    const errors = [];
    const warnings = [];

    // Email
    const email = String(data.email || '').trim();
    if (!email) errors.push('Email is required');
    else if (!isEmailValid(email)) errors.push('Email format is invalid');

    // Duplicate email
    let existingUserId = null;
    if (email && isEmailValid(email)) {
      const existing = emailToUser.get(email.toLowerCase());
      if (existing) {
        existingUserId = existing.id;
        warnings.push('Existing user — will be skipped or updated based on the option below');
      }
    }

    // Role (unknown falls back to end_user with warning)
    const rawRole = String(data.role || '').toLowerCase().trim();
    if (rawRole && !ROLE_MAP[rawRole]) {
      warnings.push(`Unknown role "${data.role}" — will default to End User`);
    }

    // Devices
    const resolvedDeviceIds = [];
    const resolvedDeviceNames = [];
    const missingDeviceNames = [];
    const rawDevices = String(data.assigned_device_names || '').trim();
    if (rawDevices) {
      // Support either ";" (our export) or "," separators
      const names = rawDevices.split(/[;,]/).map(s => s.trim()).filter(Boolean);
      names.forEach(n => {
        const dev = deviceByName.get(n.toLowerCase());
        if (dev) { resolvedDeviceIds.push(dev.id); resolvedDeviceNames.push(dev.name); }
        else missingDeviceNames.push(n);
      });
      if (missingDeviceNames.length > 0) {
        warnings.push(`${missingDeviceNames.length} device(s) not found and will be skipped: ${missingDeviceNames.join(', ')}`);
      }
    }

    // Beacons
    const resolvedBeaconIds = [];
    const resolvedBeaconNames = [];
    const missingBeaconNames = [];
    const rawBeacons = String(data.assigned_beacon_names || '').trim();
    if (rawBeacons) {
      const names = rawBeacons.split(/[;,]/).map(s => s.trim()).filter(Boolean);
      names.forEach(n => {
        const b = beaconByName.get(n.toLowerCase());
        if (b) { resolvedBeaconIds.push(b.id); resolvedBeaconNames.push(b.name); }
        else missingBeaconNames.push(n);
      });
      if (missingBeaconNames.length > 0) {
        warnings.push(`${missingBeaconNames.length} beacon(s) not found and will be skipped: ${missingBeaconNames.join(', ')}`);
      }
    }

    // Privilege / limit values — number, 'Unlimited' or 'Not Set'
    LIMIT_FIELD_KEYS.forEach(k => {
      const pv = parseLimitValue(data[k]);
      if (pv.invalid) warnings.push(`${k} value "${data[k]}" is not a number — it will be ignored`);
    });

    // Count columns are a consistency check only — allocations come from the name lists.
    const devCountRaw = String(data.assigned_device_count ?? '').trim();
    if (devCountRaw) {
      const n = parseInt(devCountRaw, 10);
      const listed = resolvedDeviceIds.length + missingDeviceNames.length;
      if (!Number.isNaN(n) && n !== listed) {
        warnings.push(`Assigned Devices Count says ${n} but ${listed} device name(s) are listed — the name list wins`);
      }
    }
    const bcnCountRaw = String(data.assigned_beacon_count ?? '').trim();
    if (bcnCountRaw) {
      const n = parseInt(bcnCountRaw, 10);
      const listed = resolvedBeaconIds.length + missingBeaconNames.length;
      if (!Number.isNaN(n) && n !== listed) {
        warnings.push(`Assigned Beacons Count says ${n} but ${listed} beacon name(s) are listed — the name list wins`);
      }
    }

    return {
      errors, warnings, existingUserId,
      resolvedDeviceIds, resolvedDeviceNames, missingDeviceNames,
      resolvedBeaconIds, resolvedBeaconNames, missingBeaconNames,
    };
  }, [emailToUser, deviceByName, beaconByName]);

  /* -------- re-run validation whenever inline edits change (or duplicate mode) */
  const reValidateRow = useCallback((row) => {
    const v = validateRow(row.data);
    return { ...row, ...v };
  }, [validateRow]);

  /* -------- file parse (CSV or Excel-safe XLSX via shared reader) */
  const handleFileSelected = async (f) => {
    if (!f) return;
    setFile(f);
    try {
      const parsed = await readUserImportFile(f);
      const parsedRows = parsed.rows.map(({ rowIndex, data }) => {
        const v = validateRow(data);
        const hasError = v.errors.length > 0;
        // Duplicate rows default-UNCHECKED so an accidental Update-mode click
        // can't silently overwrite an existing user (e.g. Super Admin).
        const isDup = !!v.existingUserId;
        return {
          id: `row-${rowIndex}`,
          rowIndex,
          data,
          selected: !hasError && !isDup,
          sendInvite: false,
          expanded: false,
          ...v,
        };
      });
      setParsedHeaders(parsed.detected);
      setIgnoredHeaders(parsed.ignored);
      setRows(parsedRows);
    } catch (err) {
      console.error('Import parse failed:', err);
      toast.error(err.message || 'Failed to parse file');
    }
  };

  /* -------- inline edit */
  const updateRowField = (rowId, field, value) => {
    setRows(prev => prev.map(r => {
      if (r.id !== rowId) return r;
      const nextData = { ...r.data, [field]: value };
      return reValidateRow({ ...r, data: nextData });
    }));
  };

  const toggleRowSelected = (rowId) => {
    setRows(prev => prev.map(r => r.id === rowId ? { ...r, selected: !r.selected } : r));
  };

  /* -------- row expansion editing helpers (devices / beacons / features) */
  const namesFrom = (s) => String(s || '').split(/[;,]/).map(x => x.trim()).filter(Boolean);

  const removeNameFromRow = (rowId, field, name) => {
    const row = rows.find(r => r.id === rowId);
    if (!row) return;
    const next = namesFrom(row.data[field]).filter(n => n.toLowerCase() !== String(name).toLowerCase());
    updateRowField(rowId, field, next.join('; '));
  };

  const addNameToRow = (rowId, field, name) => {
    const row = rows.find(r => r.id === rowId);
    if (!row) return;
    const list = namesFrom(row.data[field]);
    if (list.some(n => n.toLowerCase() === String(name).toLowerCase())) return;
    updateRowField(rowId, field, [...list, name].join('; '));
  };

  // A row can receive a welcome email only if it's a NEW user with no CSV password.
  const isInviteEligible = (r) => !r.existingUserId && !String(r.data.password || '').trim();

  const toggleRowInvite = (rowId) => {
    setRows(prev => prev.map(r => r.id === rowId ? { ...r, sendInvite: !r.sendInvite } : r));
  };

  const toggleAllInvites = (checked) => {
    setRows(prev => prev.map(r => isInviteEligible(r) ? { ...r, sendInvite: checked } : r));
  };

  const toggleRowExpanded = (rowId) => {
    setRows(prev => prev.map(r => r.id === rowId ? { ...r, expanded: !r.expanded } : r));
  };

  const toggleSelectAll = (checked) => {
    setRows(prev => prev.map(r => ({
      ...r,
      selected: checked && r.errors.length === 0,
    })));
  };

  /* -------- counters */
  const counts = useMemo(() => {
    let ready = 0, warning = 0, error = 0, duplicates = 0;
    rows.forEach(r => {
      if (r.errors.length > 0) error++;
      else if (r.warnings.length > 0) warning++;
      else ready++;
      if (r.existingUserId) duplicates++;
    });
    return { ready, warning, error, duplicates, total: rows.length };
  }, [rows]);

  const selectedRows = useMemo(() => rows.filter(r => r.selected && r.errors.length === 0), [rows]);

  const inviteEligibleRows = useMemo(() => rows.filter(isInviteEligible), [rows]);
  const allInvitesOn = inviteEligibleRows.length > 0 && inviteEligibleRows.every(r => r.sendInvite);

  /* -------- role effective */
  const effectiveRole = (raw) => {
    const key = String(raw || '').toLowerCase().trim();
    return ROLE_MAP[key] || 'end_user';
  };

  /* -------- perform import */
  const runImport = async () => {
    if (selectedRows.length === 0) {
      toast.error('No rows selected to import');
      return;
    }
    setImporting(true);
    setStep(3);
    setProgress({ current: 0, total: selectedRows.length });

    let successCreate = 0;
    let successUpdate = 0;
    let skipped = 0;
    let failed = 0;
    const errors = [];
    const undoCreated = [];
    const undoUpdated = [];
    const preview = []; // dry-run: snapshot of what WOULD be created/updated
    // Only columns actually present in the file participate in updates.
    const providedKeys = new Set(parsedHeaders.map(h => h.key));
    const PROFILE_UPDATE_KEYS = [
      'username', 'full_name', 'company', 'phone', 'mobile',
      'address_street', 'address_city', 'address_postcode', 'address_state', 'address_country',
      'notes', 'account_reference',
    ];

    for (let i = 0; i < selectedRows.length; i++) {
      const row = selectedRows[i];
      const d = row.data;
      const email = String(d.email || '').trim();
      const role = effectiveRole(d.role);
      const isDuplicate = !!row.existingUserId;

      if (dryRun) {
        // Simulate — no network, no state change. Capture a snapshot so the
        // results screen can show exactly what a real import would do.
        if (isDuplicate && duplicateMode === 'skip') {
          skipped++;
        } else {
          const limitsSummary = LIMIT_FIELD_KEYS
            .map(k => {
              const pv = parseLimitValue(d[k]);
              if (pv.invalid || pv.value === null) return null;
              return `${LIMIT_LABELS[k]}: ${pv.value === -1 ? 'Unlimited' : pv.value === 0 ? 'Not Set' : pv.value}`;
            })
            .filter(Boolean)
            .join('; ');
          preview.push({
            rowIndex: row.rowIndex,
            action: isDuplicate ? 'update' : 'create',
            email,
            full_name: d.full_name || email.split('@')[0],
            role: ROLE_LABELS[role] || role,
            devices: (row.resolvedDeviceNames || []).join('; '),
            beacons: (row.resolvedBeaconNames || []).join('; '),
            features: namesFrom(d.enabled_features).join('; '),
            limits: limitsSummary,
            password: isDuplicate
              ? 'Unchanged (existing user)'
              : d.password
                ? 'From CSV'
                : row.sendInvite
                  ? 'Welcome email — user sets own'
                  : 'Auto-generated',
          });
          if (isDuplicate) successUpdate++; else successCreate++;
        }
        setProgress({ current: i + 1, total: selectedRows.length });
        continue;
      }

      try {
        // Duplicate handling
        if (isDuplicate && duplicateMode === 'skip') {
          skipped++;
          setProgress({ current: i + 1, total: selectedRows.length });
          continue;
        }

        const basePayload = {
          email,
          full_name: d.full_name || email.split('@')[0],
          username: d.username || email.split('@')[0],
          role,
          company: d.company || '',
          phone: d.phone || '',
          mobile: d.mobile || '',
          address_street: d.address_street || '',
          address_city: d.address_city || '',
          address_postcode: d.address_postcode || '',
          address_state: d.address_state || '',
          address_country: d.address_country || 'Australia',
          notes: d.notes || '',
          account_reference: d.account_reference || '',
          is_active: !(String(d.is_active).toLowerCase() === 'blocked' || String(d.is_active).toLowerCase() === 'false' || d.is_active === false),
          expiry_enabled: String(d.expiry_enabled).toLowerCase() === 'yes' || String(d.expiry_enabled).toLowerCase() === 'true' || d.expiry_enabled === true,
          expiry_date: parseImportedDate(d.expiry_date),
        };

        let targetUserId = null;
        let undoEntry = null;
        if (isDuplicate && duplicateMode === 'update') {
          // Update existing user — only safe, contact/profile-level fields.
          // Role, is_active, and expiry are deliberately excluded so an import
          // can never demote or block an existing user (e.g. Super Admin).
          // fieldMode: 'merge' = only non-empty file values are applied;
          //            'replace' = every imported column overwrites (empty clears).
          const updatePayload = { email };
          PROFILE_UPDATE_KEYS.forEach(k => {
            if (!providedKeys.has(k)) return;
            const v = String(d[k] ?? '').trim();
            if (fieldMode === 'merge' && !v) return;
            updatePayload[k] = v;
          });
          // Snapshot pre-import values so an Undo can restore them exactly.
          let prevSnapshot = null;
          try {
            const prevRes = await api.get(`/users/${row.existingUserId}`);
            prevSnapshot = prevRes.data || null;
          } catch {
            prevSnapshot = emailToUser.get(email.toLowerCase()) || null;
          }
          await api.put(`/users/${row.existingUserId}`, updatePayload);
          targetUserId = row.existingUserId;
          successUpdate++;
          let previousFields = null;
          if (prevSnapshot) {
            previousFields = {};
            Object.keys(updatePayload).forEach(k => { previousFields[k] = prevSnapshot[k] ?? null; });
          }
          undoEntry = { user_id: targetUserId, email, previous_fields: previousFields, previous_assigned_device_ids: null, previous_assigned_beacon_ids: null };
          undoUpdated.push(undoEntry);
        } else {
          // Create new user
          if (row.sendInvite && !d.password) {
            // No CSV password + welcome email on: user sets their own password via activation link.
            const res = await api.post('/users', { ...basePayload, send_invite: true });
            targetUserId = res?.data?.id;
          } else {
            const password = d.password || `Temp${Math.random().toString(36).slice(-8)}${Math.floor(Math.random() * 10)}!`;
            const res = await api.post('/users', { ...basePayload, password });
            targetUserId = res?.data?.id;
          }
          successCreate++;
          if (targetUserId) undoCreated.push({ user_id: targetUserId, email });
        }

        // Privileges + Devices & Beacons — applied via the features endpoint.
        // A value counts as provided when its column exists in the file OR the
        // admin filled it in via the row expansion editor.
        const rowHas = (k) => providedKeys.has(k) || String(d[k] ?? '').trim() !== '';
        const wantsFeatures = rowHas('assigned_device_names') || rowHas('assigned_beacon_names')
          || rowHas('enabled_features') || LIMIT_FIELD_KEYS.some(k => rowHas(k));
        if (targetUserId && wantsFeatures) {
          try {
            const featuresRes = await api.get(`/users/${targetUserId}/features`);
            const existingF = featuresRes.data || {};
            const replaceLists = isDuplicate && duplicateMode === 'update' && fieldMode === 'replace';
            const fp = {};
            if (rowHas('assigned_device_names')) {
              const existingIds = Array.isArray(existingF.assigned_device_ids) ? existingF.assigned_device_ids : [];
              fp.assigned_device_ids = replaceLists
                ? row.resolvedDeviceIds
                : Array.from(new Set([...existingIds, ...row.resolvedDeviceIds]));
              if (undoEntry) undoEntry.previous_assigned_device_ids = existingIds;
            }
            if (rowHas('assigned_beacon_names')) {
              const existingB = Array.isArray(existingF.assigned_beacon_ids) ? existingF.assigned_beacon_ids : [];
              fp.assigned_beacon_ids = replaceLists
                ? row.resolvedBeaconIds
                : Array.from(new Set([...existingB, ...row.resolvedBeaconIds]));
              if (undoEntry) undoEntry.previous_assigned_beacon_ids = existingB;
            }
            // Limits & retention: a value is only applied when the cell has one
            // (number / 'Unlimited' / 'Not Set'). Empty cells never change limits.
            LIMIT_FIELD_KEYS.forEach(k => {
              if (!rowHas(k)) return;
              const pv = parseLimitValue(d[k]);
              if (pv.invalid || pv.value === null) return;
              fp[k] = pv.value;
            });
            if (rowHas('enabled_features')) {
              const listed = String(d.enabled_features || '').split(/[;,]/).map(s => s.trim()).filter(Boolean);
              const existingFeats = existingF.features || {};
              if (replaceLists) {
                const next = {};
                Object.keys(existingFeats).forEach(k => { next[k] = false; });
                listed.forEach(k => { next[k] = true; });
                fp.features = next;
              } else if (listed.length > 0) {
                const next = { ...existingFeats };
                listed.forEach(k => { next[k] = true; });
                fp.features = next;
              }
            }
            if (Object.keys(fp).length > 0) {
              await api.put(`/users/${targetUserId}/features`, fp);
            }
          } catch (allocErr) {
            const msg = allocErr?.response?.data?.detail || allocErr.message || 'privilege/allocation update failed';
            errors.push({ email, message: `User saved, but privileges/allocations failed: ${msg}` });
          }
        }
      } catch (err) {
        failed++;
        const msg = err?.response?.data?.detail || err.message || 'unknown error';
        errors.push({ email: email || `Row ${row.rowIndex}`, message: String(msg) });
      }
      setProgress({ current: i + 1, total: selectedRows.length });
    }

    setResults({ successCreate, successUpdate, skipped, failed, errors, dryRun, preview });
    setImporting(false);

    // Summary toast
    const parts = [];
    if (successCreate) parts.push(`${successCreate} ${dryRun ? 'would be created' : 'created'}`);
    if (successUpdate) parts.push(`${successUpdate} ${dryRun ? 'would be updated' : 'updated'}`);
    if (skipped) parts.push(`${skipped} skipped`);
    if (failed) parts.push(`${failed} failed`);
    if (dryRun) toast.info(`Dry run complete — ${parts.join(', ') || 'nothing to do'}. Nothing was saved.`);
    else if (failed === 0 && (successCreate + successUpdate) > 0) toast.success(`Import complete — ${parts.join(', ')}`);
    else if ((successCreate + successUpdate) > 0) toast.warning(`Import finished with issues — ${parts.join(', ')}`);
    else toast.error(`Import failed — ${parts.join(', ') || 'no users imported'}`);

    if (dryRun) return; // nothing was saved — no log entry, no undo manifest

    // Record in the Import/Export Log (must never block the import result)
    let ioLogId = null;
    try {
      const io = await api.post('/admin/user-io-logs', {
        kind: 'import',
        source: 'wizard',
        file_type: /\.xlsx$/i.test(file?.name || '') ? 'xlsx' : 'csv',
        file_name: file?.name || '',
        row_count: selectedRows.length,
        created: successCreate,
        updated: successUpdate,
        skipped,
        failed,
      });
      ioLogId = io?.data?.id || null;
    } catch { /* non-blocking */ }

    // Persist the run manifest so admins can Undo it (24h window).
    let runRecord = null;
    if (undoCreated.length > 0 || undoUpdated.length > 0) {
      try {
        const runRes = await api.post('/users/import-runs', {
          created: undoCreated,
          updated: undoUpdated,
          summary: { created: successCreate, updated: successUpdate, skipped, failed },
          io_log_id: ioLogId,
        });
        runRecord = runRes?.data || null;
      } catch (recErr) {
        console.error('Failed to record import run for undo:', recErr);
      }
    }

    if (typeof onCompleted === 'function') onCompleted(runRecord);
  };

  /* -------- error report download */
  const downloadErrorReport = () => {
    if (!results?.errors?.length) return;
    const rowsCsv = [
      'Email,Error',
      ...results.errors.map(e => `"${(e.email || '').replace(/"/g, '""')}","${(e.message || '').replace(/"/g, '""')}"`),
    ].join('\n');
    const blob = new Blob(['\uFEFF' + rowsCsv], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'user_import_errors.csv';
    a.click();
    window.URL.revokeObjectURL(url);
  };

  /* -------- dry-run preview downloads (CSV + Ensue-branded PDF) */
  const DRYRUN_HEADERS = ['Row #', 'Action', 'Email', 'Full Name', 'Role', 'Password', 'Devices', 'Beacons', 'Enabled Features', 'Privileges'];

  const dryRunPreviewCells = (p) => [
    p.rowIndex,
    p.action === 'update' ? 'Update' : 'Create',
    p.email,
    p.full_name,
    p.role,
    p.password || '',
    p.devices || '',
    p.beacons || '',
    p.features || '',
    p.limits || '',
  ];

  const downloadDryRunPreview = () => {
    if (!results?.preview?.length) return;
    const esc = (v) => {
      const s = String(v ?? '');
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const lines = [DRYRUN_HEADERS.join(',')];
    results.preview.forEach(p => lines.push(dryRunPreviewCells(p).map(esc).join(',')));
    const ts = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
    const blob = new Blob(['\uFEFF' + lines.join('\n') + '\n'], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `user_import_dry_run_${ts}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
    toast.success('Dry-run preview downloaded');
  };

  /* Dry-run preview HTML — branded standalone HTML table (same style as History Exports) */
  const exportDryRunHtml = async () => {
    if (!results?.preview?.length) return;
    const ts = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
    await exportTableHtml('User Import — Dry Run Preview',
      `${results.preview.length} row(s) — nothing was saved`,
      DRYRUN_HEADERS, results.preview.map(p => dryRunPreviewCells(p)), `user_import_dry_run_${ts}.html`);
    toast.success('Dry-run HTML downloaded');
  };

  const printDryRunPreview = async () => {
    if (!results?.preview?.length) { toast.error('No preview to print'); return; }
    try {
      const { doc, layout } = newLandscapeDoc();
      const { M, RIGHT, PAGE_H, CONTENT_W } = layout;
      const logo = await loadLogoPng();
      const stampStr = new Date().toLocaleString();
      let y = addBrandHeader(
        doc, layout, logo,
        'User Import — Dry Run Preview',
        `${results.successCreate} would be created · ${results.successUpdate} would be updated  ·  ${stampStr}`,
      );

      doc.setFillColor(254, 243, 199);
      doc.setDrawColor(252, 211, 77);
      doc.roundedRect(M, y - 4, 62, 16, 3, 3, 'FD');
      doc.setFontSize(9); doc.setFont(undefined, 'bold'); doc.setTextColor(146, 64, 14);
      doc.text('DRY RUN', M + 31, y + 7, { align: 'center' });
      doc.setFont(undefined, 'normal'); doc.setTextColor(...SLATE_600);
      doc.setFontSize(9);
      doc.text('Nothing was saved. This preview is for change-management sign-off only.', M + 74, y + 7);
      y += 22;

      const cardW = (CONTENT_W - 36) / 4;
      const cards = [
        ['Would create', String(results.successCreate), [22, 163, 74]],
        ['Would update', String(results.successUpdate), [37, 99, 235]],
        ['Skipped', String(results.skipped), SLATE_500],
        ['Failed', String(results.failed), [220, 38, 38]],
      ];
      cards.forEach(([label, val, color], i) => {
        const cx = M + i * (cardW + 12);
        doc.setDrawColor(...BORDER); doc.setLineWidth(0.5);
        doc.roundedRect(cx, y, cardW, 36, 4, 4, 'S');
        doc.setFontSize(8); doc.setFont(undefined, 'normal'); doc.setTextColor(...SLATE_500);
        doc.text(String(label).toUpperCase(), cx + 8, y + 12);
        doc.setFontSize(18); doc.setFont(undefined, 'bold'); doc.setTextColor(...color);
        doc.text(val, cx + 8, y + 30);
      });
      y += 46;

      const weightCols = [
        { label: '#',        key: 'rowIndex',  w: 20 },
        { label: 'Action',   key: 'action',    w: 40 },
        { label: 'Email',    key: 'email',     w: 115, wrap: true },
        { label: 'Full Name', key: 'full_name', w: 80, wrap: true },
        { label: 'Role',     key: 'role',      w: 50 },
        { label: 'Password', key: 'password',  w: 75, wrap: true },
        { label: 'Devices',  key: 'devices',   w: 90, wrap: true },
        { label: 'Beacons',  key: 'beacons',   w: 80, wrap: true },
        { label: 'Features', key: 'features',  w: 90, wrap: true },
        { label: 'Privileges', key: 'limits',  w: 110, wrap: true },
      ];
      const totalW = weightCols.reduce((s, c) => s + c.w, 0);
      const scale = CONTENT_W / totalW;
      const cols = weightCols.map(c => ({ ...c, w: c.w * scale }));
      const xs = []; let cx = M;
      cols.forEach(c => { xs.push(cx); cx += c.w; });
      const LINE_H = 10;
      const ROW_PAD = 5;

      const drawTableHeader = () => {
        doc.setFillColor(241, 245, 249);
        doc.rect(M, y, CONTENT_W, 15, 'F');
        doc.setFontSize(8); doc.setFont(undefined, 'bold'); doc.setTextColor(...SLATE_600);
        cols.forEach((c, i) => doc.text(c.label, xs[i] + 4, y + 10));
        y += 15;
      };
      drawTableHeader();
      doc.setFont(undefined, 'normal'); doc.setFontSize(8); doc.setTextColor(...SLATE_900);

      results.preview.forEach((p, i) => {
        const values = {
          rowIndex: String(p.rowIndex),
          action: p.action === 'update' ? 'Update' : 'Create',
          email: p.email || '—',
          full_name: p.full_name || '—',
          role: p.role || '—',
          password: p.password || '—',
          devices: p.devices || '—',
          beacons: p.beacons || '—',
          features: p.features || '—',
          limits: p.limits || '—',
        };
        const cellLines = cols.map(c => {
          const wrapped = doc.splitTextToSize(String(values[c.key] || ''), c.w - 8);
          return c.wrap ? wrapped.slice(0, 6) : [wrapped[0] || ''];
        });
        const maxLines = Math.max(1, ...cellLines.map(l => l.length));
        const rowH = maxLines * LINE_H + ROW_PAD;
        if (y + rowH > PAGE_H - 44) {
          doc.addPage(); y = 44;
          drawTableHeader();
          doc.setFont(undefined, 'normal'); doc.setFontSize(8); doc.setTextColor(...SLATE_900);
        }
        if (i % 2 === 1) { doc.setFillColor(248, 250, 252); doc.rect(M, y, CONTENT_W, rowH, 'F'); }
        cols.forEach((c, ci) => {
          cellLines[ci].forEach((line, li) => doc.text(line, xs[ci] + 4, y + 10 + li * LINE_H));
        });
        y += rowH;
      });

      if (y + 60 > PAGE_H - 44) { doc.addPage(); y = 44; }
      y += 20;
      doc.setDrawColor(...BORDER); doc.setLineWidth(0.5);
      doc.line(M, y, M + 240, y);
      doc.line(RIGHT - 240, y, RIGHT, y);
      doc.setFontSize(8); doc.setTextColor(...SLATE_500);
      doc.text('Prepared by (signature)', M, y + 12);
      doc.text('Approved by (signature)', RIGHT, y + 12, { align: 'right' });
      doc.setTextColor(...BLUE); doc.setFontSize(8);
      doc.text('Change-management sign-off — retain with your user onboarding paperwork.', M, y + 26);

      addFooters(doc, layout);
      const ts = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
      doc.save(`user_import_dry_run_${ts}.pdf`);
      toast.success('Dry-run PDF downloaded');
    } catch (err) {
      console.error('printDryRunPreview failed', err);
      toast.error('Failed to generate PDF');
    }
  };

  /* -------- Renderers */

  const renderStep1 = () => (
    <div className="space-y-6" data-testid="import-wizard-step1">
      <div
        className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
          dragOver
            ? 'border-blue-400 bg-blue-50/60 dark:border-blue-700 dark:bg-blue-950/20'
            : file
              ? 'border-green-300 dark:border-green-800 bg-green-50/40 dark:bg-green-950/10'
              : 'border-border'
        }`}
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragOver(false);
          const f = e.dataTransfer?.files?.[0];
          if (!f) return;
          if (!/\.(csv|xlsx)$/i.test(f.name)) {
            toast.error('Only .csv and .xlsx files are supported — drop a CSV or Excel file');
            return;
          }
          handleFileSelected(f);
        }}
        data-testid="import-wizard-drop-zone"
      >
        {file ? (
          <>
            <CheckCircle2 className="w-10 h-10 mx-auto text-green-600 mb-3" />
            <p className="text-sm font-medium mb-1" data-testid="import-wizard-file-loaded">
              File loaded — {rows.length} row(s) parsed
            </p>
            <p className="text-xs text-muted-foreground mb-4">
              Review the detected columns below, then press Next.
            </p>
          </>
        ) : (
          <>
            <Download className="w-10 h-10 mx-auto text-muted-foreground mb-3" />
            <p className="text-sm text-muted-foreground mb-4">
              Drag &amp; drop a User CSV or Excel file here, or select one (exported from EnsueLIVETRAK or matching the same column names).
            </p>
          </>
        )}
        <input
          ref={fileInputRef}
          type="file"
          accept=".csv,.xlsx,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
          className="hidden"
          data-testid="import-wizard-file-input"
          onChange={(e) => handleFileSelected(e.target.files?.[0])}
        />
        <Button
          variant="outline"
          onClick={() => fileInputRef.current?.click()}
          data-testid="import-wizard-select-file-btn"
        >
          <FileText className="w-4 h-4 mr-2" />
          {file ? 'Choose a different file' : 'Select CSV / Excel file'}
        </Button>
        {!file && (
          <div className="mt-3 flex flex-wrap items-center justify-center gap-2">
            <Button
              variant="link"
              size="sm"
              className="text-xs text-muted-foreground hover:text-foreground"
              onClick={() => downloadUserImportTemplate(true)}
              data-testid="import-wizard-download-template"
            >
              <FileText className="w-3 h-3 mr-1" />
              User Example Row (.csv)
            </Button>
            <Button
              variant="link"
              size="sm"
              className="text-xs text-[#2563eb] hover:text-[#1d4ed8]"
              onClick={() => downloadUserImportTemplateXlsx(true)}
              data-testid="import-wizard-download-xlsx-template"
            >
              <FileSpreadsheet className="w-3 h-3 mr-1" />
              User Excel-Safe Example (.xlsx)
            </Button>
          </div>
        )}
      </div>

      {file && (
        <div className="rounded-lg border p-4 space-y-3" data-testid="import-wizard-file-summary">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <FileText className="w-4 h-4 text-muted-foreground" />
              <span className="font-medium text-sm" data-testid="import-wizard-filename">{file.name}</span>
              <span className="text-xs text-muted-foreground">({(file.size / 1024).toFixed(1)} KB)</span>
            </div>
            <Badge variant="secondary" data-testid="import-wizard-row-count">{rows.length} row(s)</Badge>
          </div>

          <div>
            <p className="text-xs font-semibold text-muted-foreground mb-1">Detected columns ({parsedHeaders.length})</p>
            <div className="flex flex-wrap gap-1">
              {parsedHeaders.map(h => (
                <Badge key={h.original} variant="outline" className="text-xs" data-testid={`import-wizard-detected-col-${h.key}`}>
                  {h.original}
                </Badge>
              ))}
            </div>
          </div>

          {ignoredHeaders.length > 0 && (
            <div>
              <p className="text-xs font-semibold text-muted-foreground mb-1">Ignored columns ({ignoredHeaders.length})</p>
              <div className="flex flex-wrap gap-1">
                {ignoredHeaders.map(h => (
                  <Badge key={h} variant="secondary" className="text-xs opacity-70">{h}</Badge>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );

  const renderStep2 = () => (
    <div className="space-y-4 flex-1 min-h-0 flex flex-col" data-testid="import-wizard-step2">
      {/* Summary chips */}
      <div className="flex flex-wrap items-center gap-3">
        <Badge className="bg-green-100 text-green-800 dark:bg-green-950 dark:text-green-300" data-testid="import-wizard-count-ready">
          <CheckCircle2 className="w-3 h-3 mr-1" />Ready: {counts.ready}
        </Badge>
        <Badge className="bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-300" data-testid="import-wizard-count-warning">
          <AlertTriangle className="w-3 h-3 mr-1" />Warnings: {counts.warning}
        </Badge>
        <Badge className="bg-red-100 text-red-800 dark:bg-red-950 dark:text-red-300" data-testid="import-wizard-count-error">
          <XCircle className="w-3 h-3 mr-1" />Errors: {counts.error}
        </Badge>
        {counts.duplicates > 0 && (
          <Badge variant="secondary" data-testid="import-wizard-count-duplicates">
            Duplicates: {counts.duplicates}
          </Badge>
        )}
        <div className="ml-auto text-sm text-muted-foreground" data-testid="import-wizard-selected-count">
          Selected: {selectedRows.length} / {counts.total}
        </div>
      </div>

      {/* Welcome email for new users without a CSV password */}
      <div className="rounded-lg border p-3 bg-muted/40 flex items-start gap-3">
        <Checkbox
          id="send-welcome-email"
          checked={allInvitesOn}
          disabled={inviteEligibleRows.length === 0}
          onCheckedChange={(v) => toggleAllInvites(!!v)}
          className="mt-0.5"
          data-testid="import-wizard-send-invite"
        />
        <Label htmlFor="send-welcome-email" className="text-sm font-normal cursor-pointer leading-snug">
          <span className="font-semibold block">Send welcome email to new users without a password</span>
          <span className="text-xs text-muted-foreground">
            Tick here to select all, or choose per user in the "Welcome Email" column. Selected rows with an empty
            Password column get an activation link by email so they set their own password; unselected rows get a
            temporary password. Rows with a CSV password or existing users are not eligible.
          </span>
        </Label>
      </div>

      {/* Duplicate handling */}
      {counts.duplicates > 0 && (
        <div className="rounded-lg border p-3 bg-muted/40">
          <Label className="text-sm font-semibold mb-2 block">Duplicate emails</Label>
          <RadioGroup value={duplicateMode} onValueChange={setDuplicateMode} className="flex gap-6">
            <div className="flex items-center gap-2">
              <RadioGroupItem value="skip" id="dup-skip" data-testid="import-wizard-dup-skip" />
              <Label htmlFor="dup-skip" className="text-sm font-normal cursor-pointer">Skip existing users</Label>
            </div>
            <div className="flex items-center gap-2">
              <RadioGroupItem value="update" id="dup-update" data-testid="import-wizard-dup-update" />
              <Label htmlFor="dup-update" className="text-sm font-normal cursor-pointer">Update existing users</Label>
            </div>
          </RadioGroup>
          {duplicateMode === 'update' && (
            <div className="mt-3 pt-3 border-t" data-testid="import-wizard-field-mode">
              <Label className="text-sm font-semibold mb-2 block">How should existing values be updated?</Label>
              <RadioGroup value={fieldMode} onValueChange={setFieldMode} className="flex flex-col gap-2">
                <div className="flex items-start gap-2">
                  <RadioGroupItem value="merge" id="field-merge" className="mt-0.5" data-testid="import-wizard-field-merge" />
                  <Label htmlFor="field-merge" className="text-sm font-normal cursor-pointer leading-snug">
                    <span className="font-medium">Merge</span>
                    <span className="block text-xs text-muted-foreground">Only cells with a value are applied — empty cells keep the user's current values. Devices, beacons and features are ADDED to what the user already has.</span>
                  </Label>
                </div>
                <div className="flex items-start gap-2">
                  <RadioGroupItem value="replace" id="field-replace" className="mt-0.5" data-testid="import-wizard-field-replace" />
                  <Label htmlFor="field-replace" className="text-sm font-normal cursor-pointer leading-snug">
                    <span className="font-medium">Replace</span>
                    <span className="block text-xs text-muted-foreground">Every imported column overwrites the current value (empty cells clear it). Device, beacon and feature lists become EXACTLY what the file says.</span>
                  </Label>
                </div>
              </RadioGroup>
              <div className="mt-3 flex flex-wrap items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  className="h-7 text-xs"
                  onClick={() => setRows(prev => prev.map(r => (r.existingUserId && r.errors.length === 0) ? { ...r, selected: true } : r))}
                  data-testid="import-wizard-select-all-duplicates"
                >
                  Select all {counts.duplicates} existing user{counts.duplicates === 1 ? '' : 's'} for update
                </Button>
                <p className="text-[11px] text-amber-700 dark:text-amber-400">
                  Existing-user rows stay unticked for safety — tick the ones you want to update (or use this button), then press Import.
                </p>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Preview table */}
      <div className="flex-1 min-h-0 border rounded-lg overflow-hidden flex flex-col">
        <div className="flex-1 min-h-0 overflow-auto">
          <Table>
            <TableHeader className="sticky top-0 bg-background z-10">
              <TableRow>
                <TableHead className="w-10">
                  <Checkbox
                    checked={rows.length > 0 && rows.filter(r => r.errors.length === 0).every(r => r.selected)}
                    onCheckedChange={toggleSelectAll}
                    data-testid="import-wizard-select-all"
                  />
                </TableHead>
                <TableHead className="w-16">Status</TableHead>
                <TableHead className="w-12">#</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Full Name</TableHead>
                <TableHead>Username</TableHead>
                <TableHead>Role</TableHead>
                <TableHead>Devices / Beacons</TableHead>
                <TableHead className="w-20">Welcome Email</TableHead>
                <TableHead className="w-10" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map(row => {
                const isError = row.errors.length > 0;
                const isWarn = !isError && row.warnings.length > 0;
                const statusIcon = isError
                  ? <XCircle className="w-4 h-4 text-red-600" data-testid={`import-wizard-row-status-error-${row.id}`} />
                  : isWarn
                    ? <AlertTriangle className="w-4 h-4 text-amber-600" data-testid={`import-wizard-row-status-warning-${row.id}`} />
                    : <CheckCircle2 className="w-4 h-4 text-green-600" data-testid={`import-wizard-row-status-ready-${row.id}`} />;

                return (
                  <React.Fragment key={row.id}>
                    <TableRow className={isError ? 'bg-red-50/50 dark:bg-red-950/20' : isWarn ? 'bg-amber-50/40 dark:bg-amber-950/10' : ''}>
                      <TableCell>
                        <Checkbox
                          checked={row.selected}
                          disabled={isError}
                          onCheckedChange={() => toggleRowSelected(row.id)}
                          data-testid={`import-wizard-row-select-${row.id}`}
                        />
                      </TableCell>
                      <TableCell>{statusIcon}</TableCell>
                      <TableCell className="text-xs text-muted-foreground">{row.rowIndex}</TableCell>
                      <TableCell>
                        <Input
                          value={row.data.email || ''}
                          onChange={(e) => updateRowField(row.id, 'email', e.target.value)}
                          className="h-8 text-sm min-w-[180px]"
                          data-testid={`import-wizard-row-email-${row.id}`}
                        />
                        {row.existingUserId && (
                          <Badge variant="outline" className="mt-1 text-[10px]" data-testid={`import-wizard-row-duplicate-${row.id}`}>Existing user</Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        <Input
                          value={row.data.full_name || ''}
                          onChange={(e) => updateRowField(row.id, 'full_name', e.target.value)}
                          className="h-8 text-sm min-w-[150px]"
                          data-testid={`import-wizard-row-fullname-${row.id}`}
                        />
                      </TableCell>
                      <TableCell>
                        <Input
                          value={row.data.username || ''}
                          onChange={(e) => updateRowField(row.id, 'username', e.target.value)}
                          className="h-8 text-sm min-w-[120px]"
                          data-testid={`import-wizard-row-username-${row.id}`}
                        />
                      </TableCell>
                      <TableCell>
                        <Select
                          value={effectiveRole(row.data.role)}
                          onValueChange={(v) => updateRowField(row.id, 'role', v)}
                        >
                          <SelectTrigger className="h-8 text-sm min-w-[130px]" data-testid={`import-wizard-row-role-${row.id}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {Object.entries(ROLE_LABELS).sort((a, b) => String(a[1]).localeCompare(String(b[1]), undefined, { sensitivity: 'base' })).map(([v, l]) => (
                              <SelectItem key={v} value={v}>{l}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </TableCell>
                      <TableCell className="text-xs">
                        {row.resolvedDeviceIds.length > 0 && (
                          <div className="text-green-700 dark:text-green-400" data-testid={`import-wizard-row-devices-ok-${row.id}`}>
                            {row.resolvedDeviceIds.length} device{row.resolvedDeviceIds.length === 1 ? '' : 's'}
                          </div>
                        )}
                        {row.missingDeviceNames.length > 0 && (
                          <div className="text-amber-700 dark:text-amber-400" data-testid={`import-wizard-row-devices-missing-${row.id}`}>
                            {row.missingDeviceNames.length} device{row.missingDeviceNames.length === 1 ? '' : 's'} missing
                          </div>
                        )}
                        {(row.resolvedBeaconIds || []).length > 0 && (
                          <div className="text-cyan-700 dark:text-cyan-400" data-testid={`import-wizard-row-beacons-ok-${row.id}`}>
                            {row.resolvedBeaconIds.length} beacon{row.resolvedBeaconIds.length === 1 ? '' : 's'}
                          </div>
                        )}
                        {(row.missingBeaconNames || []).length > 0 && (
                          <div className="text-amber-700 dark:text-amber-400" data-testid={`import-wizard-row-beacons-missing-${row.id}`}>
                            {row.missingBeaconNames.length} beacon{row.missingBeaconNames.length === 1 ? '' : 's'} missing
                          </div>
                        )}
                        {row.resolvedDeviceIds.length === 0 && row.missingDeviceNames.length === 0
                          && (row.resolvedBeaconIds || []).length === 0 && (row.missingBeaconNames || []).length === 0 && (
                          <span className="text-muted-foreground">—</span>
                        )}
                      </TableCell>
                      <TableCell className="text-center">
                        {isInviteEligible(row) ? (
                          <Checkbox
                            checked={!!row.sendInvite}
                            onCheckedChange={() => toggleRowInvite(row.id)}
                            data-testid={`import-wizard-row-invite-${row.id}`}
                          />
                        ) : (
                          <span
                            className="text-muted-foreground text-xs"
                            title={row.existingUserId ? 'Existing user — keeps their password' : 'Password provided in CSV'}
                            data-testid={`import-wizard-row-invite-na-${row.id}`}
                          >—</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7"
                          onClick={() => toggleRowExpanded(row.id)}
                          data-testid={`import-wizard-row-expand-${row.id}`}
                        >
                          {row.expanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                        </Button>
                      </TableCell>
                    </TableRow>
                    {row.expanded && (
                      <TableRow>
                        <TableCell colSpan={10} className="bg-muted/30" data-testid={`import-wizard-row-details-${row.id}`}>
                          <div className="space-y-2 py-2 text-xs">
                            {row.errors.length > 0 && (
                              <div>
                                <p className="font-semibold text-red-700 dark:text-red-400 mb-1">Errors</p>
                                <ul className="list-disc list-inside space-y-0.5">
                                  {row.errors.map((e, i) => <li key={i}>{e}</li>)}
                                </ul>
                              </div>
                            )}
                            {row.warnings.length > 0 && (
                              <div>
                                <p className="font-semibold text-amber-700 dark:text-amber-400 mb-1">Warnings</p>
                                <ul className="list-disc list-inside space-y-0.5">
                                  {row.warnings.map((w, i) => <li key={i}>{w}</li>)}
                                </ul>
                              </div>
                            )}
                            {row.missingDeviceNames.length > 0 && (
                              <div>
                                <p className="font-semibold text-amber-700 dark:text-amber-400 mb-1">Missing devices (will be skipped)</p>
                                <div className="flex flex-wrap gap-1">
                                  {row.missingDeviceNames.map(n => (
                                    <Badge key={n} variant="outline" className="text-[10px] border-amber-400 text-amber-700 dark:text-amber-400">{n}</Badge>
                                  ))}
                                </div>
                              </div>
                            )}
                            {(row.missingBeaconNames || []).length > 0 && (
                              <div>
                                <p className="font-semibold text-amber-700 dark:text-amber-400 mb-1">Missing beacons (will be skipped)</p>
                                <div className="flex flex-wrap gap-1">
                                  {row.missingBeaconNames.map(n => (
                                    <Badge key={n} variant="outline" className="text-[10px] border-amber-400 text-amber-700 dark:text-amber-400">{n}</Badge>
                                  ))}
                                </div>
                              </div>
                            )}
                            <div data-testid={`import-wizard-row-allocations-${row.id}`}>
                              <p className="font-semibold mb-1">Devices &amp; Beacons to allocate <span className="font-normal text-muted-foreground">(click ✕ to remove, use the pickers to add)</span></p>
                              <div className="flex flex-wrap items-center gap-1">
                                {(row.resolvedDeviceNames || []).map(n => (
                                  <Badge key={`d-${n}`} variant="outline" className="text-[10px] border-green-400 text-green-700 dark:text-green-400 gap-1 pr-1">
                                    {n}
                                    <button
                                      type="button"
                                      className="hover:text-red-600"
                                      title="Remove device"
                                      onClick={() => removeNameFromRow(row.id, 'assigned_device_names', n)}
                                      data-testid={`import-wizard-row-remove-device-${row.id}-${n}`}
                                    >
                                      <X className="w-3 h-3" />
                                    </button>
                                  </Badge>
                                ))}
                                {(row.resolvedBeaconNames || []).map(n => (
                                  <Badge key={`b-${n}`} variant="outline" className="text-[10px] border-cyan-400 text-cyan-700 dark:text-cyan-400 gap-1 pr-1">
                                    {n} (beacon)
                                    <button
                                      type="button"
                                      className="hover:text-red-600"
                                      title="Remove beacon"
                                      onClick={() => removeNameFromRow(row.id, 'assigned_beacon_names', n)}
                                      data-testid={`import-wizard-row-remove-beacon-${row.id}-${n}`}
                                    >
                                      <X className="w-3 h-3" />
                                    </button>
                                  </Badge>
                                ))}
                                <Select key={`adddev-${row.id}-${row.data.assigned_device_names || ''}`} onValueChange={(v) => addNameToRow(row.id, 'assigned_device_names', v)}>
                                  <SelectTrigger className="h-6 w-[150px] text-[10px]" data-testid={`import-wizard-row-add-device-${row.id}`}>
                                    <SelectValue placeholder="+ Add device" />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {(allDevices || [])
                                      .filter(d => d.name && !namesFrom(row.data.assigned_device_names).some(n => n.toLowerCase() === String(d.name).toLowerCase()))
                                      .sort((a, b) => String(a.name).localeCompare(String(b.name), undefined, { sensitivity: 'base' }))
                                      .slice(0, 500)
                                      .map(d => <SelectItem key={d.id} value={d.name}>{d.name}</SelectItem>)}
                                  </SelectContent>
                                </Select>
                                <Select key={`addbcn-${row.id}-${row.data.assigned_beacon_names || ''}`} onValueChange={(v) => addNameToRow(row.id, 'assigned_beacon_names', v)}>
                                  <SelectTrigger className="h-6 w-[150px] text-[10px]" data-testid={`import-wizard-row-add-beacon-${row.id}`}>
                                    <SelectValue placeholder="+ Add beacon" />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {(allBeacons || [])
                                      .filter(b => b.name && !namesFrom(row.data.assigned_beacon_names).some(n => n.toLowerCase() === String(b.name).toLowerCase()))
                                      .sort((a, b) => String(a.name).localeCompare(String(b.name), undefined, { sensitivity: 'base' }))
                                      .slice(0, 500)
                                      .map(b => <SelectItem key={b.id} value={b.name}>{b.name}</SelectItem>)}
                                  </SelectContent>
                                </Select>
                              </div>
                            </div>
                            <div data-testid={`import-wizard-row-privileges-${row.id}`}>
                              <p className="font-semibold mb-1">Privileges to apply <span className="font-normal text-muted-foreground">(blank = leave unchanged)</span></p>
                              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                                {LIMIT_FIELD_KEYS.map(k => {
                                  const pv = parseLimitValue(row.data[k]);
                                  return (
                                    <div key={k}>
                                      <span className="text-muted-foreground block mb-0.5">{LIMIT_LABELS[k]}</span>
                                      <div className="flex items-center gap-1">
                                        <Input
                                          value={row.data[k] ?? ''}
                                          onChange={(e) => updateRowField(row.id, k, e.target.value)}
                                          className={`h-6 w-24 text-[11px] px-1.5 ${pv.invalid ? 'border-amber-400' : ''}`}
                                          placeholder="—"
                                          data-testid={`import-wizard-row-limit-${k}-${row.id}`}
                                        />
                                        <button
                                          type="button"
                                          className="text-[10px] px-1.5 py-0.5 rounded border hover:bg-muted"
                                          title="Set to Unlimited"
                                          onClick={() => updateRowField(row.id, k, 'Unlimited')}
                                          data-testid={`import-wizard-row-limit-unlimited-${k}-${row.id}`}
                                        >∞</button>
                                        <button
                                          type="button"
                                          className="text-[10px] px-1 py-0.5 rounded border hover:bg-muted"
                                          title="Set to Not Set (0)"
                                          onClick={() => updateRowField(row.id, k, 'Not Set')}
                                          data-testid={`import-wizard-row-limit-notset-${k}-${row.id}`}
                                        >NS</button>
                                      </div>
                                      {pv.invalid && <span className="text-amber-700 dark:text-amber-400 text-[10px]">invalid — ignored</span>}
                                    </div>
                                  );
                                })}
                              </div>
                              <div className="mt-1.5 flex flex-wrap items-center gap-1">
                                <span className="text-muted-foreground">Enabled features:</span>
                                {namesFrom(row.data.enabled_features).map(fk => (
                                  <Badge key={fk} variant="outline" className="text-[10px] border-blue-400 text-blue-700 dark:text-blue-400 gap-1 pr-1">
                                    {(featureCatalog.find(f => f.id === fk)?.label) || fk}
                                    <button
                                      type="button"
                                      className="hover:text-red-600"
                                      title="Remove feature"
                                      onClick={() => removeNameFromRow(row.id, 'enabled_features', fk)}
                                      data-testid={`import-wizard-row-remove-feature-${row.id}-${fk}`}
                                    >
                                      <X className="w-3 h-3" />
                                    </button>
                                  </Badge>
                                ))}
                                <Select key={`addfeat-${row.id}-${row.data.enabled_features || ''}`} onValueChange={(v) => addNameToRow(row.id, 'enabled_features', v)}>
                                  <SelectTrigger className="h-6 w-[170px] text-[10px]" data-testid={`import-wizard-row-add-feature-${row.id}`}>
                                    <SelectValue placeholder="+ Add feature" />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {featureCatalog
                                      .filter(f => !namesFrom(row.data.enabled_features).includes(f.id))
                                      .slice()
                                      .sort((a, b) => String(a.label).localeCompare(String(b.label), undefined, { sensitivity: 'base' }))
                                      .map(f => <SelectItem key={f.id} value={f.id}>{f.label}</SelectItem>)}
                                  </SelectContent>
                                </Select>
                              </div>
                            </div>
                            <div className="grid grid-cols-2 md:grid-cols-3 gap-2 mt-2">
                              <div><span className="text-muted-foreground">Account Ref:</span> {row.data.account_reference || '—'}</div>
                              <div><span className="text-muted-foreground">Company:</span> {row.data.company || '—'}</div>
                              <div><span className="text-muted-foreground">Phone:</span> {row.data.phone || '—'}</div>
                              <div><span className="text-muted-foreground">Mobile:</span> {row.data.mobile || '—'}</div>
                              <div><span className="text-muted-foreground">City:</span> {row.data.address_city || '—'}</div>
                              <div><span className="text-muted-foreground">Country:</span> {row.data.address_country || '—'}</div>
                              <div><span className="text-muted-foreground">Status:</span> {row.data.is_active || '—'}</div>
                              <div><span className="text-muted-foreground">Expiry:</span> {row.data.expiry_enabled || 'No'} {row.data.expiry_date || ''}</div>
                              <div><span className="text-muted-foreground">Password:</span> {row.data.password ? '(from CSV)' : row.sendInvite && !row.existingUserId ? '(welcome email — user sets own)' : '(auto-generated)'}</div>
                            </div>
                          </div>
                        </TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                );
              })}
            </TableBody>
          </Table>
        </div>
      </div>
    </div>
  );

  const renderStep3 = () => (
    <div className="space-y-4" data-testid="import-wizard-step3">
      {importing ? (
        <div className="flex flex-col items-center justify-center py-10 gap-3">
          <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
          <p className="text-sm font-medium" data-testid="import-wizard-progress-text">
            Importing {progress.current} of {progress.total}...
          </p>
          <div className="w-full max-w-md h-2 bg-muted rounded overflow-hidden">
            <div
              className="h-full bg-blue-600 transition-all"
              style={{ width: `${progress.total ? (progress.current / progress.total) * 100 : 0}%` }}
            />
          </div>
        </div>
      ) : results ? (
        <div className="space-y-4" data-testid="import-wizard-results">
          {results.dryRun && (
            <div className="rounded-lg border p-3 flex items-start gap-3 bg-amber-50/60 dark:bg-amber-950/20 border-amber-300 dark:border-amber-800" data-testid="import-wizard-dryrun-banner">
              <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0 text-amber-700 dark:text-amber-400" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold">Dry run &mdash; nothing was saved</p>
                <p className="text-xs text-muted-foreground">
                  This preview shows what would be created or updated. Press &ldquo;Confirm &amp; Continue&rdquo; to go back and run the real import.
                </p>
              </div>
              <div className="flex items-center gap-2 flex-wrap justify-end">
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button size="sm" variant="outline" data-testid="import-wizard-dryrun-export">
                      <Upload className="w-3.5 h-3.5 mr-1" />
                      Export
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={downloadDryRunPreview} data-testid="import-wizard-dryrun-download">CSV</DropdownMenuItem>
                    <DropdownMenuItem onClick={exportDryRunHtml} data-testid="import-wizard-dryrun-html">HTML</DropdownMenuItem>
                    <DropdownMenuItem onClick={printDryRunPreview} data-testid="import-wizard-dryrun-print">PDF</DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
                <Button
                  size="sm"
                  variant="default"
                  className="bg-[#2563eb] hover:bg-[#1d4ed8] text-white"
                  onClick={() => { setDryRun(false); setResults(null); setStep(2); }}
                  data-testid="import-wizard-dryrun-confirm"
                >
                  Confirm &amp; Continue
                </Button>
              </div>
            </div>
          )}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className="rounded-lg border p-3">
              <p className="text-xs text-muted-foreground">{results.dryRun ? 'Would create' : 'Created'}</p>
              <p className="text-2xl font-bold text-green-600" data-testid="import-wizard-result-created">{results.successCreate}</p>
            </div>
            <div className="rounded-lg border p-3">
              <p className="text-xs text-muted-foreground">{results.dryRun ? 'Would update' : 'Updated'}</p>
              <p className="text-2xl font-bold text-blue-600" data-testid="import-wizard-result-updated">{results.successUpdate}</p>
            </div>
            <div className="rounded-lg border p-3">
              <p className="text-xs text-muted-foreground">Skipped</p>
              <p className="text-2xl font-bold text-muted-foreground" data-testid="import-wizard-result-skipped">{results.skipped}</p>
            </div>
            <div className="rounded-lg border p-3">
              <p className="text-xs text-muted-foreground">Failed</p>
              <p className="text-2xl font-bold text-red-600" data-testid="import-wizard-result-failed">{results.failed}</p>
            </div>
          </div>

          {results.dryRun && (results.preview || []).length > 0 && (
            <div className="rounded-lg border overflow-hidden" data-testid="import-wizard-dryrun-preview">
              <div className="p-3 border-b bg-muted/40">
                <p className="text-sm font-semibold">Preview — exactly what would be imported</p>
              </div>
              <ScrollArea className="max-h-[420px]">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="border-b bg-muted/20 text-left">
                      <th className="p-2 font-semibold">#</th>
                      <th className="p-2 font-semibold">Action</th>
                      <th className="p-2 font-semibold">Email</th>
                      <th className="p-2 font-semibold">Name</th>
                      <th className="p-2 font-semibold">Role</th>
                      <th className="p-2 font-semibold">Password</th>
                      <th className="p-2 font-semibold">Devices / Beacons</th>
                      <th className="p-2 font-semibold">Privileges</th>
                    </tr>
                  </thead>
                  <tbody>
                    {results.preview.map((p, i) => (
                      <tr key={i} className="border-b last:border-0 align-top" data-testid={`import-wizard-dryrun-preview-row-${i}`}>
                        <td className="p-2 text-muted-foreground">{p.rowIndex}</td>
                        <td className="p-2">
                          <Badge variant="outline" className={`text-[10px] ${p.action === 'update' ? 'border-blue-400 text-blue-700 dark:text-blue-400' : 'border-green-400 text-green-700 dark:text-green-400'}`}>
                            {p.action === 'update' ? 'Update' : 'Create'}
                          </Badge>
                        </td>
                        <td className="p-2 max-w-[180px] break-all">{p.email}</td>
                        <td className="p-2 max-w-[130px] break-words">{p.full_name}</td>
                        <td className="p-2 whitespace-nowrap">{p.role}</td>
                        <td className="p-2 max-w-[130px] break-words text-muted-foreground">{p.password || '—'}</td>
                        <td className="p-2 max-w-[200px] break-words">
                          {[p.devices, p.beacons && `${p.beacons} (beacon)`].filter(Boolean).join('; ') || '—'}
                        </td>
                        <td className="p-2 max-w-[280px] break-words">
                          {[p.limits, p.features && `Features: ${p.features}`].filter(Boolean).join('; ') || '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </ScrollArea>
            </div>
          )}

          {results.errors.length > 0 && (
            <div className="rounded-lg border">
              <div className="flex items-center justify-between p-3 border-b">
                <p className="text-sm font-semibold">Errors ({results.errors.length})</p>
                <Button variant="outline" size="sm" onClick={downloadErrorReport} data-testid="import-wizard-download-errors">
                  <Download className="w-4 h-4 mr-1" />Download report
                </Button>
              </div>
              <ScrollArea className="max-h-[240px]">
                <div className="p-3 space-y-1 text-xs">
                  {results.errors.map((e, i) => (
                    <div key={i} className="flex gap-2">
                      <span className="font-medium min-w-[160px] truncate">{e.email}</span>
                      <span className="text-red-600 dark:text-red-400">{e.message}</span>
                    </div>
                  ))}
                </div>
              </ScrollArea>
            </div>
          )}
        </div>
      ) : null}
    </div>
  );

  /* -------- footer buttons per step */
  const canProceedFromStep1 = rows.length > 0 && counts.total > 0;
  const canImportFromStep2 = selectedRows.length > 0;

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o && importing) return; onOpenChange(o); }}>
      <DialogContent
        className="max-w-6xl h-[85vh] flex flex-col"
        data-testid="import-wizard-dialog"
      >
        <DialogHeader>
          <DialogTitle>Import Users</DialogTitle>
          <DialogDescription>
            Step {step} of 3 —{' '}
            {step === 1 && 'Upload & parse CSV'}
            {step === 2 && 'Review, edit & validate rows'}
            {step === 3 && (importing ? 'Importing…' : 'Result')}
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 min-h-0 flex flex-col overflow-hidden">
          {step === 1 && renderStep1()}
          {step === 2 && renderStep2()}
          {step === 3 && renderStep3()}
        </div>

        <DialogFooter className="gap-2">
          {step === 1 && (
            <>
              <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="import-wizard-cancel-step1">
                Cancel
              </Button>
              <Button
                onClick={() => setStep(2)}
                disabled={!canProceedFromStep1}
                data-testid="import-wizard-next-step1"
              >
                Next <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            </>
          )}
          {step === 2 && (
            <>
              <Button variant="outline" onClick={() => setStep(1)} data-testid="import-wizard-back-step2">
                <ArrowLeft className="w-4 h-4 mr-1" /> Back
              </Button>
              <label className="flex items-center gap-2 text-xs cursor-pointer mr-auto" data-testid="import-wizard-dryrun-label">
                <Checkbox
                  checked={dryRun}
                  onCheckedChange={(v) => setDryRun(!!v)}
                  data-testid="import-wizard-dryrun"
                />
                Dry run &mdash; preview what would happen without saving
              </label>
              <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="import-wizard-cancel-step2">
                Cancel
              </Button>
              <Button
                onClick={runImport}
                disabled={!canImportFromStep2}
                className={dryRun ? 'bg-amber-600 hover:bg-amber-700 text-white' : 'bg-[#2563eb] hover:bg-[#1d4ed8]'}
                data-testid="import-wizard-run-import"
              >
                {dryRun
                  ? `Run dry run (${selectedRows.length})`
                  : `Import ${selectedRows.length} user${selectedRows.length === 1 ? '' : 's'}`}
              </Button>
            </>
          )}
          {step === 3 && (
            <>
              {results?.dryRun && !importing && (
                <Button
                  variant="outline"
                  onClick={() => { setResults(null); setStep(2); }}
                  data-testid="import-wizard-back-step3"
                >
                  <ArrowLeft className="w-4 h-4 mr-1" /> Back to Review
                </Button>
              )}
              <Button
                onClick={() => onOpenChange(false)}
                disabled={importing}
                data-testid="import-wizard-close"
              >
                Close
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
